// "New vault" — POST /vaults (member+). On success, navigate straight into the
// new vault. Surfaces INVALID_NAME / MEMBER_REQUIRED inline.

import { cloneElement, useRef, useState, type ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { IconPlus } from "@tabler/icons-react";
import { createVault, errorText } from "../api";
import { useApi } from "../session";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";

export function CreateVaultDialog({
  onCreated,
  trigger,
}: {
  /** Called after a vault is created (e.g. to refresh a list). */
  onCreated?: () => void;
  /** Custom trigger element (rendered as the DialogTrigger); defaults to a Button. */
  trigger?: ReactElement;
} = {}) {
  const api = useApi();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Synchronous re-entrancy guard: `busy` only disables the button on the NEXT
  // render, so a fast second click (or Enter + click) can fire before React
  // re-renders. This ref blocks the duplicate the moment the first create is in
  // flight, so exactly one POST goes out per open — no duplicate vault rows.
  const inFlight = useRef(false);

  async function submit() {
    const trimmed = name.trim();
    if (inFlight.current || trimmed === "") return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const { vault } = await createVault(api, trimmed);
      // Close the modal on success so it can't be resubmitted while we navigate.
      setOpen(false);
      onCreated?.();
      navigate(`/v/${vault.id}`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        setName("");
        setError(null);
      }}
    >
      {trigger ? (
        cloneElement(trigger, { render: <DialogTrigger /> })
      ) : (
        <Button render={<DialogTrigger />}>
          <IconPlus data-icon="inline-start" />
          New vault
        </Button>
      )}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create a vault</DialogTitle>
          <DialogDescription>
            You'll be its owner. Sync a client to it, then invite others from the members panel.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="new-vault-name">Vault name</FieldLabel>
              <Input
                id="new-vault-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Field Notes"
                autoFocus
                required
              />
            </Field>
          </FieldGroup>
        </form>
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Cancel</DialogClose>
          <Button disabled={busy || name.trim() === ""} onClick={() => void submit()}>
            {busy && <Spinner />}
            Create vault
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
